import {Autocomplete, Box, TextField} from '@mui/material';
import {SyntheticEvent, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {setWellNumber} from 'slices/wellNumberSlice';
import {RouteParams} from 'utils';
import {selectWellNumber} from 'slices';
import {useMonitoringWellsData} from '@pages/newMonitoring/hooks/useMonitoringWellsData';
import i18n from 'i18n';
import {GroupHeader} from './utils';

enum ConnectionStatus {
    //КЛЮЧИ
    ACTIVE = 'digitalDrillingTab.active',
    INACTIVE = 'digitalDrillingTab.inactive',
    NO_CONNECTION = 'digitalDrillingTab.noConnection',
}

type WellSelectProps = {
    width?: number;
    label?: string;
};

type WellOption = {
    label: string;
    value?: number;
    connectionStatus: ConnectionStatus;
    minDelay?: number;
};

export const MonitoringWellSelect = ({
    width = 250,
    label = i18n.t('well', {ns: 'common'}),
}: WellSelectProps) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const params = useParams<RouteParams>();
    const wellNumber = useSelector(selectWellNumber);

    const {data: wellData} = useMonitoringWellsData();
    const [dynamicWidth, setDynamicWidth] = useState(width);

    const wellOptions = useMemo<WellOption[]>(() => {
        if (!wellData) return [];

        return wellData.map((well) => {
            const connections = well.connections ?? [];
            let connectionStatus: WellOption['connectionStatus'] = ConnectionStatus.NO_CONNECTION;
            let minDelay: number | undefined = undefined;

            if (connections.length > 0) {
                const delays = connections.map((c) => c.delayInSec ?? Infinity);
                minDelay = Math.min(...delays);

                const hasActiveConnection = connections.some(
                    (conn) =>
                        conn.active &&
                        conn.delayInSec !== undefined &&
                        conn.delayInSec <= 12 * 3600,
                );

                if (hasActiveConnection) {
                    connectionStatus = ConnectionStatus.ACTIVE;
                } else {
                    connectionStatus = ConnectionStatus.INACTIVE;
                }
            }

            return {
                label: `${well.padTitle}/${well.name}/${well.fieldTitle}`,
                value: well.id ?? 0,
                connectionStatus,
                minDelay,
            };
        });
    }, [wellData]);

    const sortedAndGroupedOptions = useMemo(() => {
        const grouped = {
            [ConnectionStatus.ACTIVE]: [] as WellOption[],
            [ConnectionStatus.INACTIVE]: [] as WellOption[],
            [ConnectionStatus.NO_CONNECTION]: [] as WellOption[],
        };

        wellOptions.forEach((option) => {
            grouped[option.connectionStatus].push(option);
        });

        return [
            ...grouped[ConnectionStatus.ACTIVE],
            ...grouped[ConnectionStatus.INACTIVE],
            ...grouped[ConnectionStatus.NO_CONNECTION],
        ];
    }, [wellOptions]);

    const currentWell = useMemo(
        () =>
            params.wellId || wellNumber
                ? wellOptions.find((well) => well.value === +(params.wellId || wellNumber))
                : null,
        [params.wellId, wellNumber, wellOptions],
    );

    useEffect(() => {
        if (currentWell?.label) {
            const charWidth = 12;
            const textWidth = currentWell.label.length * charWidth;

            if (textWidth > width) {
                setDynamicWidth(textWidth + 20);
            } else {
                setDynamicWidth(width);
            }
        }
    }, [currentWell, width]);

    const handleWellChange = (
        event: SyntheticEvent<Element, Event>,
        newWell: WellOption | null,
    ) => {
        if (newWell) {
            dispatch(setWellNumber(newWell.value));
        }

        if (!newWell) {
            dispatch(setWellNumber(''));
        }

        if (newWell && params.wellId) {
            const newPath = location.pathname
                .replace(params.wellId, `${newWell.value}`)
                .concat(location.search);

            navigate(newPath);
        }
    };

    return (
        <Autocomplete
            disablePortal
            options={sortedAndGroupedOptions}
            groupBy={(option) => option.connectionStatus}
            getOptionLabel={(option) => option.label}
            sx={{
                width: `${dynamicWidth}px`,
            }}
            value={currentWell ?? null}
            onChange={handleWellChange}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant='standard'
                    label={label}
                    size='small'
                />
            )}
            renderGroup={(params) => (
                <li key={params.key}>
                    <GroupHeader>{i18n.t(params.group, {ns: 'monitoring'})}</GroupHeader>
                    <Box component='div'>{params.children}</Box>
                </li>
            )}
        />
    );
};
